import type { AnyExtension } from '@tiptap/core';
import { StarterKit } from '@tiptap/starter-kit';
import { Markdown } from '@tiptap/markdown';
import { Placeholder } from '@tiptap/extensions';
import { TaskList } from '@tiptap/extension-task-list';
import { TaskItem } from '@tiptap/extension-task-item';
import { Image } from '@tiptap/extension-image';
import {
  AttachmentContext,
  createFileHandler,
  FileChip,
  UploadPlaceholder,
} from './attachments.tsx';
import { createSlashCommand } from './slash-menu.tsx';
import { KeyboardFocus } from './keyboard-focus.ts';
import type { RichTextEditorLabels } from './labels.ts';

interface BuildOptions {
  labels: RichTextEditorLabels;
  placeholder?: string;
  withUploads: boolean;
}

/*
 * The schema IS the feature set: everything here round-trips through markdown,
 * and nothing outside it exists. Underline is off because markdown cannot say
 * it — better no button than a mark that silently vanishes on save.
 */
export const buildEditorExtensions = (options: BuildOptions): AnyExtension[] => [
  StarterKit.configure({
    underline: false,
    heading: { levels: [1, 2, 3] },
    link: {
      openOnClick: false,
      defaultProtocol: 'https',
    },
    dropcursor: {
      color: 'var(--color-ink-muted)',
      width: 2,
    },
    /* Tab stays inside the field, so a code block has to spend it on indenting. */
    codeBlock: { enableTabIndentation: true },
  }),
  Markdown.configure({
    markedOptions: { gfm: true },
  }),
  Placeholder.configure({
    placeholder: options.placeholder ?? '',
  }),
  TaskList,
  TaskItem.configure({ nested: true }),
  Image,
  UploadPlaceholder.configure({ labels: options.labels }),
  FileChip,
  createSlashCommand(options.labels.noResults),
  KeyboardFocus,
  ...(options.withUploads ? [AttachmentContext, createFileHandler()] : []),
];
